import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataGrid } from '@mui/x-data-grid';
import { SimpleModal, Icon, BackdropLoader, toast, BasicTooltip } from '@livable/saas-react-components';
import { Typography, Button, Container, Paper, Chip } from '@mui/material';
import { Box } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { dateToFormat } from 'src/utils/date';
import { InformationBannersAddForm } from './form';
import ConfirmRemoveModal from './ConfirmRemoveModal';
import useStyles from './styles';
export const InformationBannersPage = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isEditOpen = _useState4[0],
    setIsEditOpen = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    modalIsOpen = _useState6[0],
    setModalIsOpen = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    currentId = _useState8[0],
    setCurrentId = _useState8[1];
  const fetchNotificationsDate = async () => {
    await store.notifications.getAllNotificationsAsync.run();
  };
  const removeNotification = async id => {
    const resp = await store.notifications.deleteSpecificNotificationsAsync.run({
      id: id
    });
    if (resp && resp.isSuccessful) {
      await fetchNotificationsDate();
      toast('success', {
        body: ['Successfully removed']
      });
      setModalIsOpen(false);
    }
  };
  const publishNotification = async id => {
    const resp = await store.notifications.publishNotificationAsync.run({
      id: id
    });
    if (resp && resp.isSuccessful) {
      await fetchNotificationsDate();
      toast('success', {
        body: ['Successfully published']
      });
    }
  };
  const unpublishNotification = async id => {
    const resp = await store.notifications.unpublishNotificationAsync.run({
      id: id
    });
    if (resp && resp.isSuccessful) {
      await fetchNotificationsDate();
      toast('success', {
        body: ['Successfully unpublished']
      });
    }
  };
  const handleEditModal = id => {
    setCurrentId(id);
    setIsEditOpen(true);
  };
  useEffect(() => {
    fetchNotificationsDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const renderCell = (parameter, left) => /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.cellContent, " ").concat(left && classes.cellLeft)
  }, parameter);
  const columns = [{
    field: 'title',
    headerName: t('admin_page.title'),
    width: 220,
    align: 'right',
    renderHeader: () => t('admin_page.title'),
    renderCell: params => renderCell(params.row.title, true)
  }, {
    field: 'type',
    headerName: t('admin_page.type'),
    width: 120,
    align: 'right',
    renderHeader: () => t('admin_page.type'),
    renderCell: params => renderCell(params.row.appearanceType === 'TermsOfServicesModal' ? 'ToS' : 'Banner', true)
  }, {
    field: 'startDate',
    headerName: t('admin_page.start_date'),
    width: 150,
    align: 'right',
    renderHeader: () => t('admin_page.start_date'),
    renderCell: params => renderCell(dateToFormat(params.row.startDateOnly), true)
  }, {
    field: 'endDate',
    headerName: t('admin_page.end_date'),
    width: 150,
    align: 'right',
    renderHeader: () => t('admin_page.end_date'),
    renderCell: params => renderCell(params.row.endDateOnly ? dateToFormat(params.row.endDateOnly) : '—', true)
  }, {
    field: 'status',
    headerName: t('admin_page.status'),
    width: 220,
    align: 'right',
    renderHeader: () => t('admin_page.status'),
    renderCell: params => renderCell(/*#__PURE__*/React.createElement(React.Fragment, null, params.row.isPublished && /*#__PURE__*/React.createElement(Chip, {
      label: t('admin_page.active'),
      variant: "success"
    }), !params.row.isPublished && moment(moment(params.row.startDateOnly)).diff(moment(new Date())) < 0 && /*#__PURE__*/React.createElement(Chip, {
      label: t('admin_page.paused'),
      variant: "warning"
    }), moment(moment(params.row.startDateOnly)).diff(moment(new Date())) > 0 && /*#__PURE__*/React.createElement(Chip, {
      label: 'Scheduled',
      variant: "info"
    })), true)
  }, {
    field: 'actions',
    headerName: t('admin_page.actions'),
    renderHeader: () => t('admin_page.actions'),
    width: 420,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    sortable: false,
    hideable: false,
    renderCell: params => renderCell(/*#__PURE__*/React.createElement("div", {
      className: classes.actions
    }, params.row.isPublished && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: () => unpublishNotification(params.row.id),
      'custom-attribute': '554-InformationBannersAddForm'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "pause"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, t('admin_page.pause'))), !params.row.isPublished && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: () => publishNotification(params.row.id),
      'custom-attribute': '555-InformationBannersAddForm'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "play"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, t('admin_page.activate'))), params.row.isPublished && /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('admin_page.in_order_to_update'),
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.disabledButton
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: () => handleEditModal(params.row.id),
      disabled: params.row.isPublished,
      'custom-attribute': '556-InformationBannersAddForm'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, t('admin_page.edit'))))), !params.row.isPublished && /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: () => handleEditModal(params.row.id),
      'custom-attribute': '557-InformationBannersAddForm'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, t('admin_page.edit'))), /*#__PURE__*/React.createElement(ConfirmRemoveModal, {
      bannerId: params.row.id,
      removeNotification: removeNotification,
      isOpen: modalIsOpen
    })))
  }];
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.topSectionWrapper
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Paper, {
    variant: "evenWrapper"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('admin_page.new_banner')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.addBannerBody
  }, t('admin_page.if_you_have')), /*#__PURE__*/React.createElement(SimpleModal, {
    disableEscapeKeyDown: true,
    handleClose: () => setIsOpen(false),
    handleOpen: () => setIsOpen(true),
    isOpen: isOpen,
    noCloseButton: false,
    title: t('admin_page.add_new_banner'),
    noFooter: true,
    noPadding: true,
    trigger: /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      'custom-attribute': '558-InformationBannersAddForm'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, t('admin_page.create_new_banner')))
  }, /*#__PURE__*/React.createElement(InformationBannersAddForm, {
    currentId: null
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3"
  }, t('admin_page.existing_banners'))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.lgSection, " ").concat(classes.relativeBlock)
  }, /*#__PURE__*/React.createElement(DataGrid, {
    columns: columns,
    rows: store.notifications.notificationList,
    pageSize: 10,
    rowsPerPageOptions: [5, 10, 20]
    // onPageSizeChange={newPageSize => setPageSize(newPageSize)}
    ,
    pagination: true,
    disableRowSelectionOnClick: true,
    autoHeight: true,
    columnHeaderHeight: 72,
    rowHeight: 72,
    hideFooterSelectedRowCount: true,
    slots: {
      ColumnMenuIcon: () => /*#__PURE__*/React.createElement("span", null),
      BaseButton: props => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        variant: "actionButton",
        'custom-attribute': '559-InformationBannersAddForm'
      })),
      BaseTextField: props => /*#__PURE__*/React.createElement(TextField, _extends({}, props, {
        variant: "outlined"
      }))
    },
    slotProps: {
      filterPanel: {
        filterFormProps: {
          columnInputProps: {
            variant: 'outlined'
          },
          operatorInputProps: {
            variant: 'outlined'
          }
        }
      }
    }
  }), /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: store.notifications.deleteSpecificNotificationsAsync.inProgress || store.notifications.publishNotificationAsync.inProgress || store.notifications.unpublishNotificationAsync.inProgress || store.notifications.getAllNotificationsAsync.inProgress
  }), /*#__PURE__*/React.createElement(SimpleModal, {
    disableEscapeKeyDown: true,
    handleClose: () => setIsEditOpen(false),
    handleOpen: () => setIsEditOpen(true),
    isOpen: isEditOpen,
    noCloseButton: false,
    title: t('admin_page.edit_banner'),
    noFooter: true,
    noPadding: true,
    trigger: null
  }, /*#__PURE__*/React.createElement(InformationBannersAddForm, {
    currentId: currentId,
    isOpen: isEditOpen
  })))));
});